import { useInfiniteQuery } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

export interface AuditEntryItem {
  id: string;
  event_type: string;
  status: 'success' | 'failure';
  category: string;
  created_at: string;
  ip_address?: string | null;
  user_agent?: string | null;
}

export interface AuditLogFilters {
  eventTypes: string[];
  dateFrom: string | null; // RFC3339 or null
  dateTo: string | null;
}

interface AuditError extends Error {
  status?: number;
}

const PAGE_LIMIT = 20;

export function auditLogQueryKey(f: AuditLogFilters) {
  return ['audit-log', 'list', f.eventTypes.join(','), f.dateFrom, f.dateTo] as const;
}

export function useAuditLog(filters: AuditLogFilters) {
  const api = useApi();
  const query = useInfiniteQuery({
    queryKey: auditLogQueryKey(filters),
    initialPageParam: undefined as string | undefined,
    queryFn: async ({ pageParam }) => {
      const { data, error, response } = await api.GET('/auth/account/audit-log', {
        params: {
          query: {
            ...(filters.eventTypes.length ? { event_types: filters.eventTypes.join(',') } : {}),
            ...(filters.dateFrom != null ? { date_from: filters.dateFrom } : {}),
            ...(filters.dateTo != null ? { date_to: filters.dateTo } : {}),
            ...(pageParam != null ? { cursor: pageParam } : {}),
            limit: PAGE_LIMIT,
          },
        },
      });
      if (error) {
        const e = new Error('audit_log_list_failed') as AuditError;
        e.status = response?.status;
        throw e;
      }
      return (data ?? { items: [], total: 0, next_cursor: null }) as {
        items: AuditEntryItem[];
        total: number;
        next_cursor: string | null;
      };
    },
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });

  const entries: AuditEntryItem[] = query.data?.pages.flatMap((p) => p.items) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;
  return { ...query, entries, total };
}
